/**
 * PromoterHubTool — the combined "Promoters" tool.
 *
 * Banner Ads, Promoter Visits and Resource Orders are all facets of the same
 * promoter operation, and each was quiet enough on its own to clutter the
 * sidebar. They are now tabs of this one tool (the pattern Property Discovery
 * uses), reached at /dashboard/tools/promoters.
 *
 * This tool is NAVIGATION ONLY and deliberately declares no collections: the
 * three underlying tools stay registered (nav-hidden) so the backup/export
 * screens keep their per-area granularity. `getForBackup()` only includes tools
 * that own collections, so this one is correctly absent from there.
 */

import { BaseTool } from "../BaseTool";
import { FaBullhorn } from "react-icons/fa";
import { toolRegistry } from "../ToolRegistry";

export class PromoterHubTool extends BaseTool {
  constructor() {
    super();

    this.id = "promoters";
    this.name = "Promoters";
    this.description = "Promoter visits, banner ads and resource orders";
    this.icon = FaBullhorn;
    this.color = "#10B981";
    this.path = "/dashboard/tools/promoters";

    // Nav-only: the three underlying tools own the collections.
    this.collections = [];
    this.requiresAdmin = false;

    this.summaryConfig = {
      enabled: true,
      mainStat: "todayVisits",
      expectedStatCount: 3,
    };
  }

  /**
   * Combined dashboard tile — one figure from each area, so folding three tiles
   * into one loses nothing at a glance. Each source is fetched independently and
   * a failure in one leaves the others intact.
   */
  async getSummaryData() {
    const safe = async (id) => {
      try {
        const tool = toolRegistry.get(id);
        if (!tool) return null;
        const res = await tool.getSummaryData();
        return res?.success ? res.data : null;
      } catch (e) {
        console.error(`[PromoterHub] ${id} summary failed:`, e.message);
        return null;
      }
    };

    const [visits, banners, orders] = await Promise.all([
      safe("promoter-visits"),
      safe("banners"),
      safe("orders"),
    ]);

    return {
      success: true,
      data: {
        todayVisits: visits?.todayVisits ?? 0,
        bannerClicks: banners?.totalClicks ?? banners?.clicks ?? 0,
        pendingOrders:
          orders?.pendingOrders ?? orders?.totalOrders ?? orders?.orders ?? 0,
      },
    };
  }
}

export default PromoterHubTool;
